import type { FormikValues } from 'formik'
import { useMutation } from '@apollo/client';
import { FieldArray, Formik } from 'formik';
import React, { Fragment, useState } from 'react';
import AsyncCreatableSelect from 'react-select/async-creatable';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

import { ADD_USER_SKILL } from '../../../graphql/ADD_USER_SKILL';
import { DELETE_USER_SKILL } from '../../../graphql/DELETE_USER_SKILL';
import { GET_SKILLS_BY_FILTER_QUERY } from '../../../graphql/GET_SKILLS_BY_FILTER_QUERY';
import { GET_CURRENT_USER_me_skills } from '../../../graphql/types/GET_CURRENT_USER';
import {
  IHandleOptionSelect,
  useAutocomplete
} from '../../../hooks/useAutocomplete';
import autoCompleteStyles from './autocompleteStyles';
import searchIcon from '../../../assets/svg/search.svg';

interface IUserSkillsFormProps {
  skills: GET_CURRENT_USER_me_skills[];
  hideTitle?: boolean;
}

const userSkillSchema = Yup.object().shape({
  newSkill: Yup.string().required('This field is required.')
});

const UserSkillForm: React.FunctionComponent<IUserSkillsFormProps> = ({
  skills,
  hideTitle=false
}: IUserSkillsFormProps) => {
  const [inputValue, setInputValue] = useState('');

  const [addUserSkill] = useMutation(ADD_USER_SKILL, {
    onCompleted: () => toast.success('User skill successfully added.')
  });

  const [deleteUserSkill] = useMutation(DELETE_USER_SKILL, {
    onCompleted: () => toast.success('User skill successfully deleted.')
  });

  const skillOptSelectCallback = async (params: IHandleOptionSelect) => {
    if (!params) {
      return;
    }

    const { __isNew__, label, value } = params;

    return await addUserSkill({
      variables: { skillName: __isNew__ ? value : label }
    });
  };

  const {
    loadOptions: skillsLoadingOptions,
    loading: skillsLoading
  } = useAutocomplete({
    query: GET_SKILLS_BY_FILTER_QUERY
  });

  const renderForm = ({ values, handleSubmit }: FormikValues) => (
    <form onSubmit={handleSubmit} className="m-b-1">
      <FieldArray name="skillNames">
        {arrayHelper => {
          return (
            <Fragment>
              <div className="profession-selection-container">
                <div className="profession-search-wrapper">
                  <img
                    src={searchIcon}
                    alt=""
                    className={`search-icon ${inputValue ? 'is-hidden' : ''}`}
                  />

                  <div className="profession-input-container">
                    <AsyncCreatableSelect
                      cacheOptions={true}
                      isClearable={true}
                      className="profession-async-select"
                      classNamePrefix="react-select"
                      escapeClearsValue={true}
                      backspaceRemovesValue={true}
                      isLoading={skillsLoading}
                      onChange={skillOptSelectCallback}
                      onInputChange={(value) => setInputValue(value)}
                      loadOptions={skillsLoadingOptions}
                      placeholder="Search to add"
                      styles={autoCompleteStyles}
                      components={{
                        DropdownIndicator: () => null,
                        IndicatorSeparator: () => null
                      }}
                      value={null}
                    />
                    {values.skillNames && values.skillNames.length > 0 && (
                      <div className="selected-professions-container">
                        {values.skillNames.map((name, idx) => (
                          <div key={`${idx}-${name}`} className="profession-tag">
                            <span className="profession-name">{name}</span>
                            <button
                              type="button"
                              className="remove-button"
                              onClick={async () => {
                                arrayHelper.remove(idx);
                                await deleteUserSkill({
                                variables: {
                                  skillName: name
                                }
                                });
                              }}
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Fragment>
          );
        }}
      </FieldArray>
    </form>
  );

  return (
    <Formik
      validationSchema={userSkillSchema}
      enableReinitialize={true}
      initialValues={{
        newSkill: '',
        skillNames: skills.map(skill => skill.name) || []
      }}
      onSubmit={async (values, { setSubmitting }) => {
        setSubmitting(false);
      }}
    >
      {formikData => renderForm(formikData)}
    </Formik>
  );
};

export default UserSkillForm;
